import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { isEmpty } from 'lodash';

import { closeModal, openModalAction } from '../../actions/modal';
import {
  DatePickerField,
  Input,
  SelectField,
  TextArea,
  TimePickerField,
} from '../../containers/Form';
import Strings from '../../Strings';
import Select from '../../components/Select';
import Wizard, { getTooltipFromValidationErrors } from '../../containers/Modal/Wizard';
import { actions } from '../../pages/SuperUser/Patients/redux/actions';
import { actions as scheduleActions } from '../../pages/SuperUser/Schedules/redux/actions';
import { actions as messagesActions } from '../../pages/SuperUser/Messages/redux/actions';
import { actions as orgActions } from '../../pages/Organizations/redux/actions';
import { notificationActions } from '../../components/Notification/redux/actions';
import {
  GET_CAPS_WITHOUT_SCHEDULE_RESULT,
  PATIENT_DETAILS_RESULT,
  START_TASK_RESULT,
} from '../../pages/SuperUser/Patients/redux/constants';
import { TIMEZONES } from '../../constants';
import { scheduleTypes, validateSchedule } from '../../utils/validators/schedule';
import { getDailyScheduleFrequency } from '../../utils/scheduleFrequency';
import './EditRpmScheduleModal.scss';
import { EditRpmScheduleModalPages } from '../_ModalsMetadata/ModalsMetadata';
import { getProgramDisplayName, getProgramsFromPatient } from '../../utils/cmsPrograms';
import { ORG_FEATURES, orgFeatureEnabled } from '../../utils/userPermissions';
import { getMedicationFromFormat, getScheduleDataForBackend } from '../_ModalsMetadata/Fields/scheduleFields';
import { SCHEDULE_DETAILS_RESULT } from '../../pages/SuperUser/Schedules/redux/constants';
import MessageTextArea from './MessageTextArea';
import { isLinkedPatientMedication, linkPatientMedication } from '../../utils/rpmPatientFields';
import { GET_EFFECTIVE_REMINDER_CONFIGURATION_RESULT } from '../../pages/Organizations/redux/constants';

//#region Definitions and exports
const timePerDaysTemplates = {
  onceDaily: {
    text: Strings.frequecyType.onceDaily,
    offsets: [43200], // 12 PM
  },
  twiceDaily: {
    text: Strings.frequecyType.twiceDaily,
    offsets: [28800, 64800], // 8 AM, 6 PM
    take_window: [18000, 18000], // 5 hours before/after
  },
  threeTimesDaily: {
    text: Strings.frequecyType.threeTimesDaily,
    offsets: [28800, 46800, 64800], // 8 AM, 1 PM, 6 PM
    take_window: [9000, 9000], // 2.5 hours before/after
  },
  fourTimesDaily: {
    text: Strings.frequecyType.fourTimesDaily,
    offsets: [21600, 43200, 57600, 79200], // 6 AM, 12 PM, 4 PM, 10 PM
    take_window: [7200, 7200], // 2 hours before/after
  },
  fiveTimesDaily: {
    text: Strings.frequecyType.fiveTimesDaily,
    offsets: [21600, 36000, 50400, 64800, 79200], // 6 AM, 10 AM, 2 PM, 6 PM, 10 PM
    take_window: [7200, 7200], // 2 hours before/after
  },
  sixTimesDaily: {
    text: Strings.frequecyType.sixTimesDaily,
    offsets: [21600, 32400, 43200, 54000, 64800, 75600], // 6 AM, 9 AM, 12 PM, 3 PM, 6 PM, 9 PM
    take_window: [5400, 5400], // 1.5 hours before/after
  },
  everyFourHours: {
    text: Strings.frequecyType.everyFourHours,
    offsets: [7200, 21600, 36000, 50400, 64800, 79200], // 2 AM, 6 AM, 10 AM, 2 PM, 6 PM, 10 PM
    take_window: [7200, 7200], // 2 hours before/after
  },
};

export const getWindowOffsetsWithPrefix = prefix => {
  return [
    {
      value: undefined,
      label: Strings.allDay,
    },
    ...Array.from({ length: 10 }, (_, index) => {
      const hours = (index + 1) / 2;
      return {
        value: hours * 3600,
        label: `${prefix}${hours} ${hours === 1 ? Strings.hour : Strings.hours}`,
      };
    }),
  ];
};

export const remindersTypes = {
  early: 'early',
  onTime: 'on_time',
  late: 'late',
  missedDoses: 'missed_doses',
};

export const engagementMessages = {
  first_take: 'first_take',
  streak_achievement: 'streak_achievement',
  back_on_track: 'back_on_track',
  dose_confirmed: 'dose_confirmed',
  first_dose_reminder: 'first_dose_reminder',
  daily_evening_missed_dose: 'daily_evening_missed_dose',
};

const reminderOffsets = [
  { value: 10 * 60, label: '10 min' },
  { value: 20 * 60, label: '20 min' },
  { value: 30 * 60, label: '30 min' },
  { value: 40 * 60, label: '40 min' },
  { value: 60 * 60, label: '1 hour' },
];

const defaultReminderOffsets = {
  [remindersTypes.early]: { offset: reminderOffsets[0].value },
  [remindersTypes.late]: { offset: reminderOffsets[0].value },
};

const defaultThresholds = [3, 7, 10, 30];

// Helper to determine if a reminder is enabled based on the new API format
// New API: uses explicit 'enabled' field; Old API: presence of reminder object meant enabled
const isReminderEnabled = (reminder) => {
  if (reminder === undefined || reminder === null) return false;
  if (typeof reminder.enabled === 'boolean') return reminder.enabled;
  return true;
};

export const getScheduleFromMed = med => {
  // Support both old format (med.format) and new v2 format (med.daily/interval)
  const offsets = med.daily?.offsets || med.interval?.offsets || med.format?.offset || [];
  const timezone = med.timezone || med.scheduleTimezone;
  const startTime = med.start_time || (med.format?.start_dt ? med.format.start_dt * 1000 : null);
  const endTime = med.end_time || (med.format?.end_dt ? med.format.end_dt * 1000 : null);
  const smsReminders = med.sms_reminders || med.sms_reminder;
  const milestones = med.milestone_config || med.milestone_reminders;

  return {
    id: med.id,
    offset: offsets,
    frequency: getDailyScheduleFrequency(offsets),
    name: med.name || med.medicationName,
    type: med.type || med.scheduleType,
    end_time: endTime
      ? moment(endTime).tz(timezone)
      : null,
    start_time:
      startTime && moment(startTime).isAfter(moment().endOf('day'))
        ? moment(startTime).tz(timezone)
        : null,
    timezone,
    cap_id: med.cap_id || med.capId,
    take_window: med.take_window || med.takeWindow,
    medication: med.medication || getMedicationFromFormat(med.format),
    sms_reminder: smsReminders
      ? {
        early: {
          checked: isReminderEnabled(smsReminders.early),
          offset: smsReminders.early?.offset || reminderOffsets[0].value,
          text: smsReminders.early?.text || null,
        },
        late: {
          checked: isReminderEnabled(smsReminders.late),
          offset: smsReminders.late?.offset || reminderOffsets[0].value,
          text: smsReminders.late?.text || null,
        },
        on_time: {
          checked: isReminderEnabled(smsReminders.on_time),
          text: smsReminders.on_time?.text || null,
        },
        missed_doses: {
          checked: isReminderEnabled(smsReminders.missed_doses),
          text: smsReminders.missed_doses?.text || null,
          offset: smsReminders.missed_doses?.offset || null,
        },
      }
      : defaultReminderOffsets,
    milestone_reminders: milestones
      ? {
        first_take: {
          checked: isReminderEnabled(milestones.first_take),
          text: milestones.first_take?.text || null,
        },
        streak_achievement: {
          checked: isReminderEnabled(milestones.streak_achievement),
          text: milestones.streak_achievement?.text || null,
          thresholds: milestones.streak_achievement?.thresholds || defaultThresholds,
          separateMessagesForThresholds: !isEmpty(milestones.milestone_rules?.streak?.messages),
        },
        back_on_track: {
          checked: isReminderEnabled(milestones.back_on_track),
          text: milestones.back_on_track?.text || null,
        },
        dose_confirmed: {
          checked: isReminderEnabled(milestones.dose_confirmed),
          text: milestones.dose_confirmed?.text || null,
        },
        first_dose_reminder: {
          checked: isReminderEnabled(milestones.first_dose_reminder),
          text: milestones.first_dose_reminder?.text || null,
        },
        daily_evening_missed_dose: {
          checked: isReminderEnabled(milestones.daily_evening_missed_dose),
          text: milestones.daily_evening_missed_dose?.text || null,
        },
        milestone_rules: milestones?.milestone_rules || {
          streak: {
            messages: defaultThresholds.reduce((acc, threshold) => ({ ...acc, [threshold]: null }), {})
          },
          time: med.milestone_reminders?.milestone_rules?.time,
        },
      } : {
        streak_achievement: { 
          thresholds: defaultThresholds
        },
        milestone_rules: {
          streak: {
            messages: defaultThresholds.reduce((acc, threshold) => ({ ...acc, [threshold]: null }), {})
          }
        },
      },
    enrollment_id: med.enrollment_id || med.enrollmentId,
  };
};

const MEDICATION_RECONCILIATION_TASK_ID = 16;
export const dateFarInTheFuture = '3000-01-01T23:59:59Z';

export const calculateDateInNewTimezone = (date, newTimezone) => {
  return moment(date).tz(newTimezone, true);
};

//#endregion

function EditRpmScheduleModal({ data = { ignoreFilters: false, logTask: false }, ...props }) {
  props.data = data;
  const engagementMessagesEnabled = orgFeatureEnabled(ORG_FEATURES.engagement_messages_enabled);
  const timeMilestoneMessagesEnabled = orgFeatureEnabled(ORG_FEATURES.time_milestone_messages_enabled);

  const [schedule, setSchedule] = useState(
    props.data.schedule
      ? getScheduleFromMed(props.data.schedule)
      : {
        sms_reminder: {
          ...defaultReminderOffsets,
          [remindersTypes.late]: { ...defaultReminderOffsets[remindersTypes.late], checked: true },
          [remindersTypes.missedDoses]: { checked: true },
        },
        milestone_reminders: {
          streak_achievement: { 
            thresholds: defaultThresholds,
            separateMessagesForThresholds: true,
          },
          milestone_rules: {
            streak: {
              messages: defaultThresholds.reduce((acc, threshold) => ({ ...acc, [threshold]: null }), {})
            }
          },
          time_milestones_enabled: timeMilestoneMessagesEnabled,
        },

        timezone: props.data?.patient?.timezone,
        type: scheduleTypes.everyDay,
        offset: [36000], // 10 AM
        enrollment_id: props.data.enrollmentId,
        // Opened for a patient medication (Medications table): start named after it and linked to it.
        ...(props.data.patientMedication ? {
          name: props.data.patientMedication.name,
          medication: linkPatientMedication(undefined, [props.data.patientMedication], 0),
        } : {}),
      },
  );

  const [caps, setCaps] = useState([]);
  const [defaultReminders, setDefaultReminders] = useState([]);
  const [highlightInvalidFields, sethighlightInvalidFields] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [saveBtnClicked, setSaveBtnClicked] = useState(false);
  const [programs, setPrograms] = useState(getProgramsFromPatient(props.data.patient));
  const [startDateFromEnrollment, setStartDateFromEnrollment] = useState();
  const [endDateFromEnrollment, setEndDateFromEnrollment] = useState();
  const [prefabReplies, setPrefabReplies] = useState([]);
  const [patientNotificationsEnabled, setPatientNotificationsEnabled] = useState(props.data?.patient?.smsNoticeEnabled ?? true);

  const timezones = TIMEZONES;
  const editMode = React.useMemo(() => props.data?.schedule?.id, [props.data?.schedule?.id]);
  const scheduleEnded = schedule?.end_time && moment.unix(schedule?.end_time).isBefore(moment());
  // Fresh patient details (fetched on open) for the patient medication picker; callers may pass an outdated patient.
  const [latestPatient, setLatestPatient] = useState(props.data.patient);
  const linkableMedications = React.useMemo(
    () => (latestPatient?.medications || []).filter(m => m?.name),
    [latestPatient?.medications],
  );
  const linkedMedicationIndex = linkableMedications.findIndex(m =>
    isLinkedPatientMedication(m, schedule.medication?.patient_medication, schedule.medication?.ndc_code),
  );
  const linkedMedication = linkableMedications[linkedMedicationIndex];
  // A stored link whose entry was renamed or removed from the patient's list: still shown, so it can be cleared.
  const staleLink = linkedMedicationIndex < 0 && schedule.medication?.patient_medication
    ? { value: 'linked', label: schedule.medication.ndc_code ? `${schedule.medication.patient_medication} (${schedule.medication.ndc_code})` : schedule.medication.patient_medication }
    : null;
  // An entry belongs to at most one schedule: entries another of the patient's schedules links to aren't offered.
  const otherSchedules = React.useMemo(
    () => (props.patientSchedules || []).filter(s => !props.data.schedule || s.rootId !== props.data.schedule.rootId),
    [props.patientSchedules, props.data.schedule],
  );
  const medicationOptions = linkableMedications
    .map((m, i) => ({ value: `med-${i}`, label: m.ndc ? `${m.name} (${m.ndc})` : m.name, index: i, medication: m }))
    .filter(o => o.index === linkedMedicationIndex
      || !otherSchedules.some(s => isLinkedPatientMedication(o.medication, s.format?.patient_medication, s.format?.ndc_code)));

  // allow to edit start_time only if it's in the future and editMode is true
  const allowStartTimeEdit = React.useMemo(() => editMode && getScheduleFromMed(props.data.schedule).start_time, [editMode, props.data.schedule]);
  
  const taskLogTimer = useRef();
  const taskLogData = {
    patientId: props.data.patient?.id,
    taskMetadataID: MEDICATION_RECONCILIATION_TASK_ID,
    enrollmentId: props.data.enrollmentId,
  };

  const startTask = () => {
    props.startTask(taskLogData).then(resp => {
      if (resp.type === START_TASK_RESULT) {
        taskLogTimer.current = setInterval(() => {
          props.continueTask(taskLogData);
        }, 30000);
      }
    });
  };

  const endTask = () => {
    props.endTask(taskLogData);
    clearInterval(taskLogTimer.current);
  };

  const getDataAndSetReminders = (scheduleDetails) => {
    props.getCaps().then(r => {
      if (r.type === GET_CAPS_WITHOUT_SCHEDULE_RESULT) {
        if (r.response) {
          const data = r.response;
          if (schedule.cap_id) {
            data.push(schedule.cap_id);
          }
          setCaps(data);
          if (data?.length === 1) {
            setSchedule(s => ({
              ...s,
              cap_id: data[0],
            }));
          }
        }
      }
    });

    props.getReminders(props.data.patient?.orgId, props.data.patient?.language).then(r => {
      if (r.type === GET_EFFECTIVE_REMINDER_CONFIGURATION_RESULT) {
        const early = r.response?.data?.find(m => m.type === 'early_sms');
        const onTime = r.response?.data?.find(m => m.type === 'on_time_sms');
        const late = r.response?.data?.find(m => m.type === 'late_sms');
        const missedDoses = r.response?.data?.find(m => m.type === 'missed_doses_sms');
        const firstTake = r.response?.data?.find(m => m.type === 'first_take_congratulations_sms');
        const streakAchievement = r.response?.data?.find(m => m.type === 'streak_achievement_sms');
        const backOnTrack = r.response?.data?.find(m => m.type === 'back_on_track_sms');
        const doseConfirmed = r.response?.data?.find(m => m.type === 'dose_confirmed_sms');
        const firstDoseReminder = r.response?.data?.find(m => m.type === 'first_dose_reminder_sms');
        const dailyEveningMissedDose = r.response?.data?.find(m => m.type === 'daily_evening_missed_dose_sms');
        const timeMilestoneMessages = { ...r.response?.time_milestone_messages };
        delete timeMilestoneMessages?.default;
        setDefaultReminders({
          early,
          on_time: onTime,
          late,
          missed_doses: missedDoses,
          first_take: firstTake,
          streak_achievement: streakAchievement,
          back_on_track: backOnTrack,
          dose_confirmed: doseConfirmed,
          first_dose_reminder: firstDoseReminder,
          daily_evening_missed_dose: dailyEveningMissedDose,
          time_milestone_messages: timeMilestoneMessages
        });

        if (Object.values(schedule.sms_reminder).some(re => !re?.text) || Object.values(schedule.milestone_reminders).some(re => !re?.text)) {
          setSchedule(s => ({
            ...s,
            sms_reminder: {
              ...s.sms_reminder,
              early: {
                ...s.sms_reminder?.early,
                text: s.sms_reminder?.early?.text || early.message,
              },
              on_time: {
                ...s.sms_reminder?.on_time,
                text: s.sms_reminder?.on_time?.text || onTime.message,
              },
              late: {
                ...s.sms_reminder?.late,
                text: s.sms_reminder?.late?.text || late.message,
              },
              missed_doses: {
                ...s.sms_reminder?.missed_doses,
                text: s.sms_reminder?.missed_doses?.text || missedDoses.message,
              },
            },
            milestone_reminders: {
              first_take: {
                ...s.milestone_reminders?.first_take,
                text: s.milestone_reminders?.first_take?.text || firstTake.message,
              },
              streak_achievement: {
                ...s.milestone_reminders?.streak_achievement,
                text: s.milestone_reminders?.streak_achievement?.text || streakAchievement.message,
                separateMessagesForThresholds: scheduleDetails 
                  ? !isEmpty(scheduleDetails.milestone_reminders?.milestone_rules?.streak?.messages)
                  : s.milestone_reminders?.streak_achievement?.separateMessagesForThresholds,
              },
              back_on_track: {
                ...s.milestone_reminders?.back_on_track,
                text: s.milestone_reminders?.back_on_track?.text || backOnTrack.message,
              },
              dose_confirmed: {
                ...s.milestone_reminders?.dose_confirmed,
                text: s.milestone_reminders?.dose_confirmed?.text || doseConfirmed?.message,
              },
              first_dose_reminder: {
                ...s.milestone_reminders?.first_dose_reminder,
                text: s.milestone_reminders?.first_dose_reminder?.text || firstDoseReminder?.message,
              },
              daily_evening_missed_dose: {
                ...s.milestone_reminders?.daily_evening_missed_dose,
                text: s.milestone_reminders?.daily_evening_missed_dose?.text || dailyEveningMissedDose?.message,
              },
              milestone_rules: {
                ...s.milestone_reminders?.milestone_rules,
                streak: {
                  ...s.milestone_reminders?.milestone_rules?.streak,
                  ...scheduleDetails?.milestone_reminders?.milestone_rules?.streak,
                  messages: {
                    ...s.milestone_reminders?.milestone_rules?.streak?.messages,
                    ...scheduleDetails?.milestone_reminders?.milestone_rules?.streak?.messages,
                    ...(s.milestone_reminders?.streak_achievement?.thresholds || defaultThresholds).reduce(
                      (acc, threshold) => ({ 
                        ...acc, 
                        [threshold]: scheduleDetails?.milestone_reminders?.milestone_rules?.streak?.messages?.[threshold]
                          ?? s.milestone_reminders?.milestone_rules?.streak?.messages?.[threshold]
                          ?? streakAchievement.message
                      }), 
                      {}
                    )
                  }
                },
                time: {
                  ...scheduleDetails?.milestone_reminders?.milestone_rules?.time,
                  messages: Object.entries(timeMilestoneMessages || {}).reduce(
                    (acc, [day, dayMessages]) => ({
                      ...acc,
                      [day]: {
                        ...(dayMessages || {}),
                        ...(scheduleDetails?.milestone_reminders?.milestone_rules?.time?.messages?.[day] || {}),
                      },
                    }),
                    { ...(scheduleDetails?.milestone_reminders?.milestone_rules?.time?.messages || {}) },
                  ),
                },
              },
              time_milestones_enabled: scheduleDetails?.milestone_reminders?.time_milestones_enabled,
            },
          }));
        }
      }
    });

    props.getPatientInfo(props.data.patient?.id).then(r => {
      if (r.type === PATIENT_DETAILS_RESULT) {
        if (r.response) {
          const p = r.response;
          setLatestPatient(p);
          const enrollments = getProgramsFromPatient(p);
          const defaultEnrollment = enrollments?.find(e => e.isDefault)?.enrollmentId;
          setPrograms(enrollments);
          setPatientNotificationsEnabled(p.smsNoticeEnabled);
          if (!schedule.id && defaultEnrollment) {
            setSchedule(s => ({
              ...s,
              enrollment_id: schedule.enrollment_id || defaultEnrollment,
            }));
          }
        }
      }
    });

    props.getPrefabReplies().then(r => {
      setPrefabReplies(r.response || []);
    });
  }

  useEffect(() => {
    if (props.data.logTask && props.data.enrollmentId) {
      startTask();
    }

    if (props.data?.schedule?.id) {
      props.getScheduleDetails(props.data.schedule.rootId).then(r => {
        if (r.type === SCHEDULE_DETAILS_RESULT) {
          getDataAndSetReminders(r.response);
        }
      });
    } else {
      getDataAndSetReminders();
    }

    return () => {
      if (taskLogTimer?.current) {
        clearInterval(taskLogTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const allowedTagsForReminders = Object.entries(defaultReminders).reduce(
      (acc, r) => ({ ...acc, [r[0]]: r[1]?.actions }),
      {},
    );
    setValidationErrors(validateSchedule(schedule, allowedTagsForReminders, scheduleEnded));
  }, [schedule, defaultReminders]);

  useEffect(() => {
    if (schedule.enrollment_id) {
      const programDetails = programs.find(p => p.enrollmentId === schedule.enrollment_id);
      if (programDetails) {
        if (!editMode) {
          // if enrollment is not started yet (no enrollmentStart or enrollmentStart is in the future),
          // set start_date to far in the future so that backend automatically start it when enrollment is activated
          const proposedStart = moment(programDetails.enrollmentStart).isBefore(moment())
            ? null
            : !programDetails.enrollmentStart || moment(programDetails.enrollmentStart).isAfter(moment().endOf('day'))
            ? moment(dateFarInTheFuture)
            : moment();
          setStartDateFromEnrollment(proposedStart);
        }
        if (
          programDetails.enrollmentEnd &&
          !editMode &&
          moment(programDetails.enrollmentStart).isAfter(moment().endOf('day'))
        ) {
          setEndDateFromEnrollment(moment(programDetails.enrollmentEnd));
        }
      }
    } else {
      setStartDateFromEnrollment(null);
      setEndDateFromEnrollment(null);
    }
  }, [schedule.enrollment_id]);

  useEffect(() => {
    // if schedule start_time is set in the past, error
    if (
      allowStartTimeEdit &&
      schedule.start_time &&
      schedule.start_time.isBefore(moment.tz(schedule.timezone).startOf('day'))
    ) {
      setValidationErrors(v => [
        ...v,
        {
          property: 'start_time',
          errors: [Strings.errors.startDateCantBeInThePast],
        },
      ]);
    }

    // if start_time is after end_time, error
    if (schedule.start_time && schedule.end_time && schedule.start_time.isAfter(schedule.end_time)) {
      setValidationErrors(v => [
        ...v,
        {
          property: 'end_time',
          errors: [Strings.errors.endDateMustBeAfterStartDate],
        },
      ]);
    }

    // if end_time is after enrollment end, error
    const enrollmentEndDate = programs.find(p => p.enrollmentId === schedule.enrollment_id)?.enrollmentEnd;
    if (schedule.end_time && enrollmentEndDate && schedule.end_time.isAfter(enrollmentEndDate)) {
      setValidationErrors(v => [
        ...v,
        {
          property: 'end_time',
          errors: [Strings.errors.endDateMustBeBeforeEnrollmentEndDate],
        },
      ]);
    }
  }, [schedule, defaultReminders]);

  const onDateChange = (date, field) => {
    let dateToset = date;
    if (date) {
      if (field === 'start_time') {
        dateToset = date.clone().startOf('day');
      }
      if (field === 'end_time') {
        dateToset = date.clone().endOf('day');
      }
    }
    if (field === 'start_time' && date === '') {
      setStartDateFromEnrollment(null);
    }
    if (field === 'end_time' && date === '') {
      setEndDateFromEnrollment(null);
    }
    setSchedule(p => ({
      ...p,
      [field]: dateToset,
    }));
  };

  const onSelectedOption = (option, field) => {
    setSchedule(p => ({
      ...p,
      [field]: option.value,
    }));
  };

  const onTypeSelected = option => {
    const template = timePerDaysTemplates[option.value];
    setSchedule(s => ({
      ...s,
      type: template ? scheduleTypes.everyDay : scheduleTypes.asNeeded,
      frequency: option.value,
      offset: template ? [...template.offsets] : [],
      take_window: template?.take_window ? [...template.take_window] : undefined,
      invalidFields: [],
    }));
  };

  // Links the schedule to a patient medications list entry (null = none, only when editing). A new schedule is named
  // after its entry; an existing schedule's name doesn't change.
  const onPatientMedicationChosen = index => {
    setSchedule(s => {
      const medication = linkPatientMedication(s.medication, linkableMedications, index);
      return editMode ? { ...s, medication } : { ...s, name: linkableMedications[index].name, medication };
    });
  };

  const onWindowSelected = (field, value) => {
    setSchedule(s => ({
      ...s,
      take_window: [
        field === 'start' ? value : s.take_window?.[0] || null,
        field === 'end' ? value : s.take_window?.[1] || null,
      ],
    }));
  };

  const onTimezoneChange = option => {
    setSchedule(s => ({
      ...s,
      timezone: option.value,
      end_time: s.end_time ? calculateDateInNewTimezone(s.end_time, option.value) : null,
    }));
  };

  const onOffsetChange = (value, index) => {
    setSchedule(s => ({
      ...s,
      offset: s.offset.map((o, i) => {
        if (i === index) {
          return moment.duration(value).asSeconds();
        }
        return o;
      }),
      invalidFields: !s.invalidFields ? [] : s.invalidFields.filter(f => !(f.field === 'offset' && f.index === index)),
    }));
  };

  const selectedFrequency = schedule.type === scheduleTypes.asNeeded
    ? scheduleTypes.asNeeded
    : schedule.frequency || getDailyScheduleFrequency(schedule.offset);

  const onReminderChange = (group, type, field, value) => {
    setSchedule(s => ({
      ...s,
      [group]: {
        ...s[group],
        [type]: {
          ...s[group]?.[type],
          [field]: value,
        },
      },
    }));
  };

  const setInvalidFields = (field, index) => {
    setSchedule(s => ({
      ...s,
      invalidFields: !s.invalidFields
        ? [{ field, index }]
        : s.invalidFields.some(f => f.field === field && f.index === index)
          ? s.invalidFields
          : s.invalidFields.concat({ field, index }),
    }));
  };

  const onSave = async () => {
    if (saveBtnClicked) {
      return;
    }
    setSaveBtnClicked(true);

    const patientId = props.data.patient.id;
    const scheduleWithDatesFixed = {...schedule, start_time: schedule.start_time || startDateFromEnrollment, end_time: schedule.end_time || endDateFromEnrollment };
    const requestData = getScheduleDataForBackend(scheduleWithDatesFixed, props.data.schedule ? getScheduleFromMed(props.data.schedule) : undefined, defaultReminders);

    // Enrollment handling for v2 schedules API:
    // - "No Program" is represented as null -> send clear_enrollment=true and omit enrollment_id
    // - Any selected program -> ensure clear_enrollment is not present
    if (requestData.enrollment_id === null) {
      requestData.clear_enrollment = true;
      delete requestData.enrollment_id;
    } else {
      delete requestData.clear_enrollment;
    }

    // End date handling: null means the user explicitly cleared a previously
    // assigned end date. Send clear_end_time=true so the backend can
    // distinguish "no change" (omitted) from "clear" (explicit null).
    if (requestData.end_time === null) {
      requestData.clear_end_time = true;
      delete requestData.end_time;
    } else {
      delete requestData.clear_end_time;
    }

    if (isEmpty(requestData)) {
      props.showNotification(Strings.errors.nothingToUpdate, 5000, true);
      setSaveBtnClicked(false);
      return;
    }

    try {
      let scheduleResponse;
      if (editMode) {
        scheduleResponse = await props.editSchedule(patientId, props.data.schedule.rootId, requestData);
      } else {
        scheduleResponse = await props.addSchedule(patientId, requestData);
      }
      if (scheduleResponse && scheduleResponse.type === `${props.data.actionType}/error`) {
        props.showNotification(scheduleResponse.response?.data?.standard_error?.message || scheduleResponse.response?.data?.error?.message || Strings.errors.unknownError, 5000, true);
        setSaveBtnClicked(false);
        return;
      }
      if (requestData.sms_reminders || requestData.milestone_reminders) {
        const smsRequest = {};

        if (requestData.sms_reminders) {
          Object.values(remindersTypes).forEach(reminder => {
            smsRequest[`${reminder}_enabled`] = requestData.sms_reminders[reminder]?.enabled || false;
          });
        }
        if (requestData.milestone_reminders) {
          Object.values(engagementMessages).forEach(reminder => {
            smsRequest[`${reminder}_enabled`] = !!requestData.milestone_reminders?.[reminder];
          });
        }
        const remindersResp = await props.editScheduleReminders(patientId, scheduleResponse.response.root_id, smsRequest);
        if (remindersResp && remindersResp.type === `${props.data.actionType}/error`) {
          props.showNotification(remindersResp.response?.data?.error?.message, 5000, true);
          setSaveBtnClicked(false);
          return;
        }
      }
      if (props.data.logTask && props.data.enrollmentId) {
        endTask();
      }
      props.showNotification(Strings.success.medicationSaved);
      props.closeModalWithNextAction();
    } catch (error) {
      props.showNotification(
        error.response?.data?.standard_error?.message || error.response?.data?.error?.message || Strings.errors.unknownError,
        5000,
        true,
      );
    } finally {
      setSaveBtnClicked(false);
    }
  };

  const onCancel = () => {
    if (props.data.logTask && props.data.enrollmentId) {
      endTask();
    }
  };

  const confirmCloseWithoutMedication = () => {
    props.openConfirmModal({
      title: Strings.capPatient.noMedicationSchedule,
      text: props.data.closeWarning,
      cancelText: Strings.patient_medications.addScheduleTitle,
      confirmText: Strings.capPatient.closeAnyway,
      onConfirm: () => {
        onCancel();
        props.closeModal();
      },
    });
    return false;
  };

  // closeWarning marks an enrollment still missing a schedule; saving to another programme would leave it so.
  const onSubmit = () => {
    if (!props.data.closeWarning || schedule.enrollment_id === props.data.enrollmentId) {
      onSave();
      return;
    }
    const enrollment = programs.find(p => p.enrollmentId === props.data.enrollmentId);
    props.openConfirmModal({
      title: Strings.capPatient.noMedicationSchedule,
      text: Strings.formatString(Strings.capPatient.medicationNotLinkedWarning, getProgramDisplayName(enrollment?.name, enrollment?.subprogram)),
      cancelText: Strings.back,
      confirmText: Strings.capPatient.saveAnyway,
      onConfirm: onSave,
    });
  };

  const setMedicationText = key => e => {
    const { value } = e.target;
    setSchedule(s => ({ ...s, medication: { ...s.medication, [key]: value } }));
  };

  // A new schedule is only for one of the patient's medications list entries that has no schedule yet: the entry names
  // it and gives its NDC. A schedule added for a given entry (the Medications table's Add schedule) keeps that entry.
  const newSchedulePatientMedicationField = () => {
    if (props.data.patientMedication && linkedMedication) {
      return (
        <Input
          name="patient_medication"
          id="patient_medication"
          label={Strings.patient_medications.patientMedication}
          value={medicationOptions.find(o => o.index === linkedMedicationIndex)?.label || linkedMedication.name}
          readOnly
          disabled
        />
      );
    }
    if (medicationOptions.length === 0) {
      return (
        <div className="notification-off-warning" data-testid="no-medications-to-schedule">
          <div className="icon-container">
            <div className="warning-icon"></div>
          </div>
          <div>
            {linkableMedications.length === 0
              ? Strings.patient_medications.noPatientMedications
              : Strings.patient_medications.noMedicationsToSchedule}
          </div>
        </div>
      );
    }
    return (
      <SelectField
        name="patient_medication"
        id="patient_medication"
        label={Strings.patient_medications.patientMedication}
        value={linkedMedicationIndex >= 0 ? `med-${linkedMedicationIndex}` : ''}
        data={medicationOptions.map(({ value, label }) => ({ value, label }))}
        placeholder={Strings.patient_medications.selectPatientMedication}
        onChange={option => onPatientMedicationChosen(parseInt(option.value.replace('med-', ''), 10))}
        isRequired
        highlightInvalid={highlightInvalidFields}
      />
    );
  };

  // An existing schedule's drug can't change; its link is shown read-only, or offered while unlinked or stale. With the
  // link equal to the schedule's name, the field adds nothing.
  const editedSchedulePatientMedicationField = () => {
    if (linkedMedication) {
      if (schedule.name === linkedMedication.name) {
        return null;
      }
      return (
        <Input
          name="patient_medication"
          id="patient_medication"
          label={Strings.patient_medications.patientMedication}
          value={medicationOptions.find(o => o.index === linkedMedicationIndex)?.label || linkedMedication.name}
          readOnly
          disabled
        />
      );
    }
    if (medicationOptions.length === 0 && !staleLink) {
      return null;
    }
    return (
      <SelectField
        name="patient_medication"
        id="patient_medication"
        label={Strings.patient_medications.patientMedication}
        value={staleLink?.value || 'none'}
        data={[
          { value: 'none', label: Strings.patient_medications.noPatientMedication },
          ...(staleLink ? [staleLink] : []),
          ...medicationOptions.map(({ value, label }) => ({ value, label })),
        ]}
        onChange={option => {
          if (option.value !== 'linked') {
            onPatientMedicationChosen(option.value === 'none' ? null : parseInt(option.value.replace('med-', ''), 10));
          }
        }}
      />
    );
  };

  const medicationPage = (
    <>
      {editMode && <div className="page-info-header">{schedule.name}</div>}
      {editMode ? editedSchedulePatientMedicationField() : newSchedulePatientMedicationField()}
      <Input
        name="sig"
        id="sig"
        label={Strings.patient_medications.sig}
        value={schedule.medication?.sig || ''}
        onChange={setMedicationText('sig')}
      />
      <TextArea
        name="medication_notes"
        id="medication_notes"
        label={Strings.patient_medications.medicationNotes}
        value={schedule.medication?.notes || ''}
        onChange={setMedicationText('notes')}
        rows={3}
      />
    </>
  );

  const schedulePage = (
    <>
      <div className="page-info-header">{schedule.name}</div>
      <SelectField
        name="type"
        id="type"
        value={selectedFrequency || ''}
        label={Strings.doseFrequency}
        placeholder={schedule.type === scheduleTypes.everyDay ? Strings.everyDay : Strings.select}
        onChange={onTypeSelected}
        data={[
          ...Object.keys(timePerDaysTemplates).map(key => ({ value: key, label: timePerDaysTemplates[key].text })),
          { value: scheduleTypes.asNeeded, label: Strings.asNeeded },
        ]}
        isRequired
        highlightInvalid={highlightInvalidFields}
        disabled={editMode}
      />
      {schedule.type && (
        <>
          {allowStartTimeEdit && schedule.start_time && schedule.start_time?.unix() !== moment(dateFarInTheFuture).unix() && (
            <DatePickerField
              name="start_time"
              id="start_time"
              label={Strings.startDate}
              value={schedule.start_time || startDateFromEnrollment || ''}
              timezone={schedule.timezone}
              onChange={e => onDateChange(e, 'start_time')}
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'start_time')?.errors || []}
              disabled
            />
          )}
          <div className="horizontal-flex">
            {editMode && (
              <DatePickerField
                name="end_time"
                id="end_time"
                label={Strings.endDate}
                value={schedule.end_time || endDateFromEnrollment || ''}
                timezone={schedule.timezone}
                onChange={e => onDateChange(e, 'end_time')}
                highlightInvalid={highlightInvalidFields}
                errorsForTooltip={validationErrors.find(v => v.property === 'end_time')?.errors || []}
                disabled={scheduleEnded}
              />
            )}
            <SelectField
              name="schedule_timezone"
              id="schedule_timezone"
              label={Strings.timezone}
              value={schedule.timezone}
              placeholder={Strings.select}
              onChange={onTimezoneChange}
              data={timezones.map(tz => ({
                label: tz.text,
                value: tz.timezone,
              }))}
              isRequired
              highlightInvalid={highlightInvalidFields}
            />
          </div>
          {schedule.type === scheduleTypes.everyDay && (
            <>
              <div className="horizontal-flex">
                <div className="vertical-flex">
                  {schedule.offset &&
                    schedule.offset.length > 0 &&
                    schedule.offset.map((o, i) => (
                      <TimePickerField
                        key={`offset_${i}`}
                        id={`offset_${i}`}
                        name={`offset_${i}`}
                        label={`${Strings.time} ${i + 1}`}
                        value={!isNaN(o) ? moment.unix(o).utc() : undefined}
                        onChange={e => onOffsetChange(e, i)}
                        onInvalidValue={() => setInvalidFields('offset', i)}
                        isRequired
                        highlightInvalid={highlightInvalidFields}
                        errorsForTooltip={
                          validationErrors.find(v => v.property === 'offset' && v.index === i)?.errors || []
                        }
                      />
                    ))}
                </div>
                <div />
              </div>
              <div className="horizontal-flex">
                <SelectField
                  name="administration_window_start"
                  id="administration_window_start"
                  label={Strings.administrationWindow}
                  value={schedule.take_window?.[0]}
                  placeholder={Strings.select}
                  onChange={e => onWindowSelected('start', e.value)}
                  data={getWindowOffsetsWithPrefix('-')}
                  isRequired={schedule.offset?.length > 1 || schedule.take_window?.[1]}
                  highlightInvalid={highlightInvalidFields}
                  errorsForTooltip={
                    validationErrors.find(v => v.property === 'take_window' && v.field === 'start')?.errors || []
                  }
                />
                <SelectField
                  name="administration_window_end"
                  id="administration_window_end"
                  value={schedule.take_window?.[1]}
                  placeholder={Strings.select}
                  onChange={e => onWindowSelected('end', e.value)}
                  data={getWindowOffsetsWithPrefix('+')}
                  isRequired={schedule.offset?.length > 1 || schedule.take_window?.[0]}
                  highlightInvalid={highlightInvalidFields}
                  errorsForTooltip={
                    validationErrors.find(v => v.property === 'take_window' && v.field === 'end')?.errors || []
                  }
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );

  const notificationsOffWarning = patientNotificationsEnabled ? null : (
    <div className="notification-off-warning">
      <div className="icon-container">
        <div className="warning-icon"></div>
      </div>
      <div>
        {Strings.patient_medications.notificationsOffWarning}
      </div>
    </div>
  );

  const remindersPage = (
    <>
      <div className="page-info-header">{schedule.name}</div>
      {notificationsOffWarning}
      {Object.values(remindersTypes).map((r, i) => (
        <React.Fragment key={`reminder_${r}`}>
          <div className={`horizontal-flex reminders-row reminder-${r}`}>
            <Input
              type="checkbox"
              name={`reminder_check_${i}`}
              id={`reminder_check_${i}`}
              label={Strings.patient_medications.reminders[r]}
              defaultChecked={schedule.sms_reminder?.[r]?.checked}
              onChange={e => onReminderChange('sms_reminder', r, 'checked', e.target.checked)}
            />
            {r === remindersTypes.onTime ? (
              <></>
            ) : r === remindersTypes.missedDoses ? (
              <Input
                type="number"
                id={`reminder_doses_${i}`}
                label=""
                placeholder={Strings.patient_medications.defaultDoses}
                disabled={!schedule.sms_reminder?.[r]?.checked}
                value={schedule.sms_reminder?.[r]?.offset}
                errorsForTooltip={
                  validationErrors.find(v => v.property === 'sms_reminder' && v.type === r && v.field === 'offset')
                    ?.errors || []
                }
                highlightInvalid={highlightInvalidFields && schedule.sms_reminder?.[r]?.checked}
                onChange={e => onReminderChange('sms_reminder', r, 'offset', parseInt(e.target.value, 10))}
              />
            ) : (
              <Select
                name={`reminder_time_${i}`}
                id={`reminder_time_${i}`}
                value={schedule.sms_reminder?.[r]?.offset}
                placeholder={Strings.select}
                onChange={e => onReminderChange('sms_reminder', r, 'offset', e.value)}
                data={reminderOffsets}
                disabled={!schedule.sms_reminder?.[r]?.checked}
              />
            )}
          </div>
          {schedule.sms_reminder?.[r]?.checked && (
            <MessageTextArea
              group="sms_reminder"
              type={r}
              schedule={schedule}
              onScheduleChange={setSchedule}
              defaultReminders={defaultReminders}
              validationErrors={validationErrors}
              highlightInvalidFields={highlightInvalidFields}
              prefabReplies={prefabReplies}
            />
          )}
          <div className="hr" />
        </React.Fragment>
      ))}
      <div>{Strings.patient_medications.reminderCharLimit}</div>
    </>
  );

  const engagementMessagesPage = (
    <>
      <div className="page-info-header">{schedule.name}</div>
      {notificationsOffWarning}
      {Object.values(engagementMessages).map((r, i) => (
        <React.Fragment key={`reminder_${r}`}>
          <div className={`horizontal-flex reminders-row reminder-${r}`}>
            <Input
              type="checkbox"
              name={`reminder_check_${i}`}
              id={`reminder_check_${i}`}
              label={Strings.patient_medications.reminders[r]}
              defaultChecked={schedule.milestone_reminders?.[r]?.checked}
              onChange={e => onReminderChange('milestone_reminders', r, 'checked', e.target.checked)}
            />
          </div>
          {r === engagementMessages.streak_achievement && schedule.milestone_reminders?.[r]?.checked && (
            <>
              <SelectField
                name="streak_achievement_separateMessagesForThresholds"
                id="streak_achievement_separateMessagesForThresholds"
                label={Strings.patient_medications.messageSettings}
                placeholder={Strings.select}
                value={schedule.milestone_reminders?.streak_achievement?.separateMessagesForThresholds}
                onChange={option => {onReminderChange('milestone_reminders', r, 'separateMessagesForThresholds', option.value)}}
                data={[
                  { value: true, label: Strings.patient_medications.useSeparateMessage },
                  { value: false, label: Strings.patient_medications.useSameMessage },
                ]}
              />
              <div>{Strings.thresholds}</div>
              {schedule.milestone_reminders?.streak_achievement?.separateMessagesForThresholds ? 
                schedule.milestone_reminders?.[r]?.thresholds?.map((t, idx) => (
                  <React.Fragment key={`threshold_fragment_${idx}`}>
                    <div className="horizontal-flex unequal-children">
                      <Input
                        key={`reminder_threshold_${r}_${idx}`}
                        id={`reminder_threshold_${r}_${idx}`}
                        type="number"
                        disabled={!schedule.milestone_reminders?.[r]?.checked}
                        value={t}
                        isRequired={schedule.milestone_reminders?.[r]?.checked}
                        errorsForTooltip={
                          validationErrors.find(
                            v => v.property === 'milestone_reminders' && v.type === r && v.index === idx
                          )?.errors || []
                        }
                        highlightInvalid={
                          highlightInvalidFields && schedule.milestone_reminders?.[r]?.checked
                        }
                        onChange={e => {
                          const oldThreshold = schedule.milestone_reminders?.[r]?.thresholds?.[idx];
                          const newThreshold = parseInt(e.target.value, 10);
                          const newThresholds = [...(schedule.milestone_reminders?.[r]?.thresholds || [])];
                          newThresholds[idx] = newThreshold;
                          onReminderChange('milestone_reminders', r, 'thresholds', newThresholds);
                          
                          // Update milestone_rules.streak.messages when threshold value changes
                          if (oldThreshold !== newThreshold) {
                            setSchedule(s => {
                              const newMessages = { ...s.milestone_reminders?.milestone_rules?.streak?.messages };
                              if (oldThreshold && newMessages[oldThreshold]) {
                                // Move the message to the new threshold key
                                newMessages[newThreshold] = newMessages[oldThreshold];
                                delete newMessages[oldThreshold];
                              } else if (!newMessages[newThreshold]) {
                                // Set default message for new threshold
                                newMessages[newThreshold] = defaultReminders?.streak_achievement?.message || null;
                              }
                              return {
                                ...s,
                                milestone_reminders: {
                                  ...s.milestone_reminders,
                                  milestone_rules: {
                                    ...s.milestone_reminders?.milestone_rules,
                                    streak: {
                                      ...s.milestone_reminders?.milestone_rules?.streak,
                                      messages: newMessages
                                    }
                                  }
                                }
                              };
                            });
                          }
                        }}
                      />
                      <div className="button-container">
                        <button
                          type="button"
                          className="minus-button"
                          onClick={() => {
                            const newThresholds = schedule.milestone_reminders?.[r]?.thresholds?.filter((_, filterIdx) => filterIdx !== idx);
                            const removedThreshold = schedule.milestone_reminders?.[r]?.thresholds?.[idx];
                            onReminderChange('milestone_reminders', r, 'thresholds', newThresholds);
                            
                            // Remove the corresponding message from milestone_rules.streak.messages
                            if (removedThreshold) {
                              setSchedule(s => {
                                const newMessages = { ...s.milestone_reminders?.milestone_rules?.streak?.messages };
                                delete newMessages[removedThreshold];
                                return {
                                  ...s,
                                  milestone_reminders: {
                                    ...s.milestone_reminders,
                                    milestone_rules: {
                                      ...s.milestone_reminders?.milestone_rules,
                                      streak: {
                                        ...s.milestone_reminders?.milestone_rules?.streak,
                                        messages: newMessages
                                      }
                                    }
                                  }
                                };
                              });
                            }
                          }}
                          disabled={schedule.milestone_reminders?.[r]?.thresholds?.length <= 1}
                        />
                      </div>
                    </div>
                    <div key={`threshold_message_${idx}`}>
                      <MessageTextArea
                        group="milestone_reminders"
                        type={`${r}_threshold_${idx}`}
                        threshold={t}
                        thresholdIdx={idx}
                        schedule={schedule}
                        onScheduleChange={setSchedule}
                        defaultReminders={defaultReminders}
                        validationErrors={validationErrors}
                        highlightInvalidFields={highlightInvalidFields}
                        prefabReplies={prefabReplies}
                      />
                    </div>
                  </React.Fragment>
                )) : (
                  <div className='reminders-thresholds'>
                    {schedule.milestone_reminders?.[r]?.thresholds?.map((t, idx) => (
                      <div className="horizontal-flex unequal-children">
                        <Input
                          key={`reminder_threshold_${r}_${idx}`}
                          id={`reminder_threshold_${r}_${idx}`}
                          type="number"
                          disabled={!schedule.milestone_reminders?.[r]?.checked}
                          value={t}
                          isRequired={schedule.milestone_reminders?.[r]?.checked}
                          errorsForTooltip={
                            validationErrors.find(
                              v => v.property === 'milestone_reminders' && v.type === r && v.index === idx
                            )?.errors || []
                          }
                          highlightInvalid={
                            highlightInvalidFields && schedule.milestone_reminders?.[r]?.checked
                          }
                          onChange={e => {
                            const newThresholds = [...(schedule.milestone_reminders?.[r]?.thresholds || [])];
                            newThresholds[idx] = parseInt(e.target.value, 10);
                            onReminderChange('milestone_reminders', r, 'thresholds', newThresholds);
                          }}
                        />
                        <div className="button-container">
                          <button
                            className="minus-button"
                            onClick={() => onReminderChange('milestone_reminders', r, 'thresholds', schedule.milestone_reminders?.[r]?.thresholds?.filter((_, i) => i !== idx))}
                            disabled={schedule.milestone_reminders?.[r]?.thresholds?.length <= 1}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              <div className="button-container ">
                <button
                  type="button"
                  className="plus-button"
                  onClick={() => {
                    const newThresholds = (schedule.milestone_reminders?.[r]?.thresholds?.sort((a, b) => a - b) || []).concat(['']);
                    onReminderChange('milestone_reminders', r, 'thresholds', newThresholds);
                    
                    // Add a new message entry for the new threshold when it gets a value
                    // We'll set it when the threshold input gets a valid number
                  }}
                />
              </div>
            </>
          )}
          {schedule.milestone_reminders?.[r]?.checked && 
            (r !== engagementMessages.streak_achievement || !schedule.milestone_reminders?.streak_achievement?.separateMessagesForThresholds) && (
            <MessageTextArea
              group="milestone_reminders"
              type={r}
              schedule={schedule}
              onScheduleChange={setSchedule}
              defaultReminders={defaultReminders}
              validationErrors={validationErrors}
              highlightInvalidFields={highlightInvalidFields}
              prefabReplies={prefabReplies}
            />
          )}
          <div className="hr" />
        </React.Fragment>
      ))}
      <div>{Strings.patient_medications.reminderCharLimit}</div>
    </>
  );

  const timeMilestonesPage = (
    <>
      <div className="page-info-header">{schedule.name}</div>
      {notificationsOffWarning}
      <div className={`horizontal-flex reminders-row`}>
        <Input
          type="checkbox"
          name="time_milestones_enabled"
          id="time_milestones_enabled"
          label={Strings.capPatient.adherenceMilestonesMessages}
          defaultChecked={schedule.milestone_reminders?.time_milestones_enabled}
          onChange={e => setSchedule(s => ({
            ...s,
            milestone_reminders: {
              ...s.milestone_reminders,
              time_milestones_enabled: e.target.checked,
            },
          }))}
        />
      </div>
      {schedule.milestone_reminders?.time_milestones_enabled && (
        <>
          {[7, 14, 21, 28].map(day => (
            <React.Fragment key={`time_milestone_${day}`}>
              <div className="milestone-day-header">
                <label>{day} Days Achievement</label>
              </div>
              {['compliant', 'partial'].map(complianceType => (
                <MessageTextArea
                  key={`time_${day}_${complianceType}`}
                  group="milestone_rules"
                  type={`time_${day}_${complianceType}`}
                  schedule={schedule}
                  onScheduleChange={setSchedule}
                  defaultReminders={defaultReminders}
                  validationErrors={validationErrors}
                  highlightInvalidFields={highlightInvalidFields}
                  prefabReplies={prefabReplies}
                />
              ))}
              <div className="hr" />
            </React.Fragment>
          ))}
          <div>{Strings.patient_medications.reminderCharLimit}</div>
        </>
      )}
    </>
  );

  const assignCapPage = (
    <>
      <div className="page-info-header">{schedule.name}</div>
      <SelectField
        name="cap_id"
        id="cap_id"
        label={Strings.capId}
        defaultValue={schedule.cap_id}
        placeholder={Strings.select}
        onChange={e => onSelectedOption(e, 'cap_id')}
        data={caps.map(c => ({
          label: c,
          value: c,
        }))}
      />
    </>
  );

  const linkProgramPage = (
    <>
      <div className="page-info-header">{schedule.name}</div>
      <SelectField
        name="program"
        id="program"
        label={Strings.program}
        defaultValue={schedule?.enrollment_id || null}
        placeholder={Strings.select}
        onChange={e => onSelectedOption(e, 'enrollment_id')}
        data={[{ label: Strings.capPatient.noProgram, value: null }].concat(
          programs?.map(c => ({
            label: getProgramDisplayName(c.name, c.subprogram),
            value: c.enrollmentId,
          })),
        )}
      />
    </>
  );

  const medicationFields = ['name'];
  const scheduleFields = ['type', 'offset', 'take_window', 'end_time', 'timezone', 'start_time'];
  const remindersFields = ['sms_reminder'];
  const engagementMessagesFields = ['milestone_reminders'];
  const timeMilestonesFields = ['milestone_rules'];

  const pages = [
    {
      id: EditRpmScheduleModalPages.medication.id,
      title: editMode ? Strings.patient_medications.editScheduleTitle : EditRpmScheduleModalPages.medication.name,
      content: medicationPage,
      emptyFieldsCount: validationErrors.filter(v => medicationFields.includes(v.property) && v.missing).length,
      canGoNext: validationErrors.filter(v => medicationFields.includes(v.property)).length === 0,
      closeButton: { text: props.data.workflow ? Strings.skipAndClose : Strings.close },
    },
    {
      id: EditRpmScheduleModalPages.linkProgram.id,
      title: EditRpmScheduleModalPages.linkProgram.name,
      content: linkProgramPage,
      validations: [],
      canGoNext: true,
    },
    {
      id: EditRpmScheduleModalPages.schedule.id,
      title: EditRpmScheduleModalPages.schedule.name,
      content: schedulePage,
      emptyFieldsCount: validationErrors.filter(v => scheduleFields.includes(v.property) && v.missing).length,
      canGoNext: validationErrors.filter(v => scheduleFields.includes(v.property)).length === 0,
    },
    {
      id: EditRpmScheduleModalPages.reminders.id,
      title: EditRpmScheduleModalPages.reminders.name,
      content: remindersPage,
      emptyFieldsCount: validationErrors.filter(v => remindersFields.includes(v.property) && v.missing).length,
      canGoNext: validationErrors.filter(v => remindersFields.includes(v.property)).length === 0,
    },
    {
      id: EditRpmScheduleModalPages.engagementMessages.id,
      title: EditRpmScheduleModalPages.engagementMessages.name,
      content: engagementMessagesPage,
      emptyFieldsCount: validationErrors.filter(v => engagementMessagesFields.includes(v.property) && v.missing).length,
      canGoNext: validationErrors.filter(v => engagementMessagesFields.includes(v.property)).length === 0,
    },
    {
      id: EditRpmScheduleModalPages.timeMilestones.id,
      title: EditRpmScheduleModalPages.timeMilestones.name,
      content: timeMilestonesPage,
      emptyFieldsCount: validationErrors.filter(v => timeMilestonesFields.includes(v.property) && v.missing).length,
      canGoNext: validationErrors.filter(v => timeMilestonesFields.includes(v.property)).length === 0,
    },
    {
      id: EditRpmScheduleModalPages.assignCap.id,
      title: EditRpmScheduleModalPages.assignCap.name,
      content: assignCapPage,
      validations: [],
      canGoNext: !saveBtnClicked,
    },
  ];

  const getpagesToShow = () => {
    let ret =
      props.data.pages && props.data.pages.length > 0
        ? props.data.pages.map(p => pages.find(pg => pg.id === p.id))
        : pages;
    if (!engagementMessagesEnabled) {
      ret = ret.filter(p => p.id !== EditRpmScheduleModalPages.engagementMessages.id);
    }
    if (!timeMilestoneMessagesEnabled) {
      ret = ret.filter(p => p.id !== EditRpmScheduleModalPages.timeMilestones.id);
    }
    if (schedule.type === scheduleTypes.asNeeded) {
      ret = ret.filter(p => 
        p.id !== EditRpmScheduleModalPages.reminders.id 
        && p.id !== EditRpmScheduleModalPages.engagementMessages.id 
        && p.id !== EditRpmScheduleModalPages.timeMilestones.id
      );
    }
    if (programs.length === 0) {
      ret = ret.filter(p => p.id !== EditRpmScheduleModalPages.linkProgram.id);
    }
    if (props.data.ignoreFilters) {
      return ret;
    }
    return ret.filter(p => props.pagesVisibility?.some(pg => pg.id === p.id && pg.visible));
  };

  return (
    <Wizard
      name="edit-rpm-schedule"
      pages={getpagesToShow()}
      onNextButtonHover={e => sethighlightInvalidFields(e)}
      onSubmit={onSubmit}
      onCancelAction={onCancel}
      onBeforeCancel={props.data.closeWarning ? confirmCloseWithoutMedication : undefined}
      showPagesFilter={!props.data.ignoreFilters}
      nextButtonTooltip={getTooltipFromValidationErrors(validationErrors)}
    />
  );
}

EditRpmScheduleModal.propTypes = {
  data: PropTypes.shape({
    pages: PropTypes.array,
    schedule: PropTypes.any,
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      language: PropTypes.string,
      medications: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          ndc: PropTypes.string,
          enrollmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          enrollment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
      ),
    }),
    actionType: PropTypes.string,
    workflow: PropTypes.string,
    ignoreFilters: PropTypes.bool,
    logTask: PropTypes.bool,
    enrollmentId: PropTypes.string,
    closeWarning: PropTypes.string,
    patientMedication: PropTypes.shape({ name: PropTypes.string, ndc: PropTypes.string, sig: PropTypes.string, notes: PropTypes.string }),
  }),
  onSubmit: PropTypes.func,
  patientSchedules: PropTypes.arrayOf(PropTypes.object),
  getCaps: PropTypes.func,
  getReminders: PropTypes.func,
  showNotification: PropTypes.func,
  pagesVisibility: PropTypes.array,
  closeModalWithNextAction: PropTypes.func,
  closeModal: PropTypes.func,
  openConfirmModal: PropTypes.func,
  startTask: PropTypes.func,
  continueTask: PropTypes.func,
  endTask: PropTypes.func,
  getPatientInfo: PropTypes.func,
  getPrefabReplies: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    conditions: state.superUser.cohorts?.conditions,
    facilities: state.superUser.cohorts?.facilities,
    cohorts: state.superUser.cohorts?.cohorts,
    pagesVisibility: state.modalsVisibility.pagesVisibility.find(m => m.id === 'edit-rpm-schedule').pages,
    // The patient's schedules as loaded for the Medications tab (also loaded by the onboarding wizard before activation).
    patientSchedules: state.superUser?.patients?.patientMedications?.medications,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getScheduleDetails: scheduleId => dispatch(scheduleActions.getScheduleDetails(ownProps.data.patient.id, scheduleId)),
  editSchedule: (patientId, scheduleId, data) => dispatch(scheduleActions.editSchedule(patientId, scheduleId, data)),
  addSchedule: (patientId, data) => dispatch(scheduleActions.addSchedule(patientId, data)),
  closeModalWithNextAction: () => {
    dispatch(closeModal('edit-rpm-schedule'));
    if (ownProps.data.onSuccess) {
      ownProps.data.onSuccess(ownProps.data.patient.id);
    }
  },
  closeModal: () => dispatch(closeModal('edit-rpm-schedule')),
  openConfirmModal: data => dispatch(openModalAction('new-confirmation-modal', data)),
  editScheduleReminders: (patientId, scheduleId, data) => dispatch(scheduleActions.editScheduleReminders(patientId, scheduleId, data)),
  getCaps: () => dispatch(actions.getCapsWithoutSchedule(ownProps.data?.patient?.id)),
  getReminders: (orgId, language) => dispatch(orgActions.getEffectiveReminderConfiguration(orgId, language)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
  startTask: data => dispatch(actions.startTask(data)),
  endTask: data => dispatch(actions.endTask(data)),
  continueTask: data => dispatch(actions.continueTask(data)),
  getPatientInfo: patientId => dispatch(actions.getPatientInfo(patientId)),
  getPrefabReplies: () => dispatch(messagesActions.getPrefabReplies()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditRpmScheduleModal);
